
import Vue from "vue";
import VueI18n from "vue-i18n";
import ComparisonRadarChartComponent, { ComparisonAssessmentItem, ComparisonBenchmarkItem } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";

import { TypedVue } from "@/store/types";
import { Component, Inject, Prop } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { ProjectTheme, ProjectReportData, EvaluationReportData, V2Project } from "@/store/admin/adminTypes";
import { ColorMap } from "@/lib/charts/HighChartThemes";
import { SchemasCandidateAssessment } from "@/lib/serviceTypes";
import { TRAIT_CLASS_RANGES, Trait, FOUR_GROUP_TRAIT_GROUPINGS } from "@/lib/consts";
import { getOrientationText, getTraitContent } from "@/lib/elraReport7DynamicText";
import { Getters } from "@/store/admin/adminGetters";
import { Actions } from "@/store/admin/adminActions";
import { Actions as InternalActions } from "@/store/internal/interalActions";
import { Roles } from "@/lib/auth/User";
import { buildCandidateSummaries, buildStatusScoring, CandidateSummary, PARTICIPANT_TYPE_LABELS } from "./shared";
import OrientationCard, { OrientationCardData } from "./OrientationCard.vue";
import InsightEditBox from "./InsightEditBox.vue";
import ParticipantTag from "./ParticipantTag.vue";
import CandidatePicker from "@/components/project-reports/CandidatePicker.vue";

type InsightField = "system3Insights" | "searchPartnerInsights";

// Display order (S, P, O, C) and copy match the participant report's orientations
// section; ids are FOUR_GROUP_TRAIT_GROUPINGS keys ("poeple-orientation" matches
// the existing typo in consts.ts).
const ORIENTATION_DEFS = [
  { id: "strategic-orientation", letter: "S", label: "Strategic Orientation", subtitle: "How much a leader invests in big-picture, future-oriented thinking." },
  { id: "poeple-orientation", letter: "P", label: "People Orientation", subtitle: "How much a leader focuses on supporting others' growth and well-being and championing social impact." },
  { id: "outcome-orientation", letter: "O", label: "Outcome Orientation", subtitle: "How much a leader prioritises pursuing targets, enforcing process, and persisting through obstacles." },
  { id: "communication-orientation", letter: "C", label: "Communication Orientation", subtitle: "How much a leader communicates to share information, build relationships, and influence others." },
];

const namespace = "admin";

@Component({
  components: {
    ComparisonRadarChartComponent,
    OrientationCard,
    InsightEditBox,
    ParticipantTag,
    CandidatePicker,
  },
})
export default class AnalysisTab extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true })
  readonly report!: ProjectReportData;

  @Prop({ required: true })
  readonly project!: V2Project;

  @Prop({ required: true })
  readonly reportData!: EvaluationReportData;

  @Prop({ required: true })
  readonly sortedAssessments!: EvaluationReportData["assessments"];

  @Prop({ default: false })
  readonly printing!: boolean;

  @Getter(Getters.GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID, { namespace })
  public rootBenchmarkId!: string;

  @Action(Actions.GET_PROJECT_REPORT_DATA, { namespace })
  getProjectReportData!: ({ reportId, raw }: { reportId: string; raw?: boolean }) => Promise<ProjectReportData>;

  @Action(InternalActions.UPDATE_REPORT, { namespace: "internal" })
  updateReport!: (data: { reportId: string; name: string; description: string | undefined; assessmentIds: string[]; data: Record<string, unknown> }) => Promise<void>;

  public comparisonCandidates: ComparisonAssessmentItem[] = [];
  public comparisonBenchmarks: ComparisonBenchmarkItem[] = Vue.observable([]);
  public selectedCandidateId: string | null = null;
  public openState: Record<string, boolean> = {};
  // "<candidateId>:<field>" → true while an empty box is open from "+ Add notes".
  public addingNote: Record<string, boolean> = {};

  get projectTheme(): ProjectTheme {
    return (this.project?.theme as ProjectTheme) ?? ProjectTheme.DEFAULT_TA;
  }

  // Every participant, candidates first — feeds the radar chart's "add to chart" list,
  // so managers and team members can still be overlaid on a candidate's profile.
  get allSummaries(): CandidateSummary[] {
    const all = buildCandidateSummaries(this.sortedAssessments, this.reportData);
    return [...all.filter((s) => s.isCandidate), ...all.filter((s) => !s.isCandidate)];
  }

  // Candidates only — the profile selector; managers and team members have no profile here.
  get summaries(): CandidateSummary[] {
    return this.allSummaries.filter((s) => s.isCandidate);
  }

  get selectedSummary(): CandidateSummary | null {
    return this.summaries.find((s) => s.id === this.selectedCandidateId) ?? null;
  }

  get visibleSummaries(): CandidateSummary[] {
    if (this.printing) return this.summaries;
    return this.selectedSummary ? [this.selectedSummary] : [];
  }

  // One radar item set per candidate for printing, each with that candidate selected.
  // Built once (computed) so the chart components get stable arrays to mutate.
  get printRadarSets(): Record<string, ComparisonAssessmentItem[]> {
    const out: Record<string, ComparisonAssessmentItem[]> = {};
    this.summaries.forEach((summary) => {
      out[summary.id] = Vue.observable(this.buildRadarItems(summary.id));
    });
    return out;
  }

  radarCandidatesFor(candidateId: string): ComparisonAssessmentItem[] {
    return this.printing ? this.printRadarSets[candidateId] ?? [] : this.comparisonCandidates;
  }

  orientationCardsFor(summary: CandidateSummary): OrientationCardData[] {
    const statusScoring = buildStatusScoring(summary.traitScores);

    return ORIENTATION_DEFS.map((def) => {
      const traits = (FOUR_GROUP_TRAIT_GROUPINGS[def.id] as Trait[]).map((trait) => {
        const range = TRAIT_CLASS_RANGES[trait];
        const { definition, behaviours } = getTraitContent(trait, this.$i18n);
        return {
          key: trait,
          name: this.$t(`Comparison::TraitChart::XAxisLabel::${trait}`).toString(),
          score: summary.traitScores[trait] ?? 0,
          status: summary.traitInfo[trait]?.status ?? ("neutral" as const),
          benchLow: range.low,
          benchHigh: range.high,
          definition,
          behaviours,
        };
      });

      return {
        ...def,
        traits,
        texts: getOrientationText(def.id, statusScoring, this.$i18n),
        prioritizedCount: traits.filter((t) => t.status !== "neutral").length,
      };
    });
  }

  get canEditSystem3(): boolean {
    return this.$auth.user?.hasRole(Roles.GLOBAL_ADMIN) || false;
  }

  get canEditPartner(): boolean {
    return this.canEditSystem3 || this.$auth.user?.hasRole(Roles.ORGANIZATION_ADMIN) || false;
  }

  system3Insight(candidateId: string): string {
    return this.reportData.customText?.system3Insights?.[candidateId] ?? "";
  }

  partnerInsight(candidateId: string): string {
    return this.reportData.customText?.searchPartnerInsights?.[candidateId] ?? "";
  }

  // Boxes only show when they have content, or while a note is being added via "+ Add notes".
  showSystem3Box(candidateId: string): boolean {
    return !!this.system3Insight(candidateId).trim() || this.isAddingNote(candidateId, "system3Insights");
  }

  showPartnerBox(candidateId: string): boolean {
    return !!this.partnerInsight(candidateId).trim() || this.isAddingNote(candidateId, "searchPartnerInsights");
  }

  // System-3 users add to the System-3 box; client admins add to the Search Partner box.
  get addNotesField(): InsightField | null {
    if (this.canEditSystem3) return "system3Insights";
    if (this.canEditPartner) return "searchPartnerInsights";
    return null;
  }

  showAddNotes(candidateId: string): boolean {
    if (this.printing || !this.addNotesField) return false;
    return this.addNotesField === "system3Insights" ? !this.showSystem3Box(candidateId) : !this.showPartnerBox(candidateId);
  }

  isAddingNote(candidateId: string, field: InsightField): boolean {
    return !this.printing && !!this.addingNote[`${candidateId}:${field}`];
  }

  startAddingNote(candidateId: string, field: InsightField | null): void {
    if (field) this.$set(this.addingNote, `${candidateId}:${field}`, true);
  }

  stopAddingNote(candidateId: string, field: InsightField): void {
    this.$delete(this.addingNote, `${candidateId}:${field}`);
  }

  isOrientOpen(id: string): boolean {
    return this.printing || !!this.openState[id];
  }

  selectCandidate(identifier: string): void {
    this.selectedCandidateId = identifier;
    this.comparisonCandidates.forEach((item) => (item.selected = item.id === identifier));
    this.resetOpenState();
  }

  // Same interaction as the participant report: plain click opens only the clicked
  // orientation; shift-click toggles it while keeping the others (never emptying the
  // set — closing the last open panel via shift-click keeps it open instead).
  onOrientClick(id: string, event: MouseEvent): void {
    if (event.shiftKey) {
      const next = !this.openState[id];
      this.$set(this.openState, id, next);
      if (!next && ORIENTATION_DEFS.every((def) => !this.openState[def.id])) {
        this.$set(this.openState, id, true);
      }
    } else {
      ORIENTATION_DEFS.forEach((def) => this.$set(this.openState, def.id, def.id === id));
    }
  }

  closeOrient(id: string): void {
    this.$set(this.openState, id, false);
  }

  resetOpenState(): void {
    ORIENTATION_DEFS.forEach((def, i) => this.$set(this.openState, def.id, i === 0));
  }

  // Persists one insight field. The loaded report data has assessments joined in by the
  // read pipeline, so re-fetch the stored document (raw) and update that — never echo
  // the joined `data.assessments` back through the update endpoint.
  async saveInsight(candidateId: string, field: InsightField, value: string): Promise<void> {
    const raw = await this.getProjectReportData({ reportId: this.report.identifier, raw: true });
    const rawData = raw.data as EvaluationReportData;
    const record = { ...(rawData.customText?.[field] ?? {}), [candidateId]: value };
    const customText = { ...rawData.customText, [field]: record };

    await this.updateReport({
      reportId: this.report.identifier,
      name: raw.name,
      description: raw.description,
      assessmentIds: raw.assessmentIds ?? [],
      data: { ...rawData, customText },
    });

    if (!this.reportData.customText) {
      this.$set(this.reportData, "customText", {});
    }
    this.$set(this.reportData.customText, field, record);
  }

  mounted() {
    this.comparisonBenchmarks = Vue.observable<ComparisonBenchmarkItem[]>(
      this.project.benchmarks?.map(
        (b) =>
          ({
            data: b as any,
            id: b.identifier,
            hidden: false,
            highlighted: false,
            locked: false,
            selected: this.rootBenchmarkId === b.identifier || false,
            color: b.colour || ColorMap[this.projectTheme].default,
            initialColor: b.colour || ColorMap[this.projectTheme].default,
          } as ComparisonBenchmarkItem),
      ) ?? [],
    );

    // Default to the top-ranked candidate.
    this.selectedCandidateId = this.summaries[0]?.id ?? null;
    this.resetOpenState();
    this.comparisonCandidates = Vue.observable<ComparisonAssessmentItem[]>(this.buildRadarItems(this.selectedCandidateId));
  }

  // Radar items for every participant with `selectedId` pre-selected. Managers / team
  // members are labelled so they read as context in the chart's add-to-chart list.
  buildRadarItems(selectedId: string | null): ComparisonAssessmentItem[] {
    return this.allSummaries.map(
      (s) =>
        ({
          data: {
            ...(s.assessment as unknown as SchemasCandidateAssessment),
            assessmentId: s.id,
            traitScores: s.assessment.scoring.trait_scores,
            readinessScore: s.assessment.scoring.readiness,
            candidateName: s.isCandidate ? s.name : `${s.name} (${PARTICIPANT_TYPE_LABELS[s.participantType]})`,
            dateCompleted: s.assessment.dateCompleted ?? (s.assessment as unknown as { date_completed?: string }).date_completed,
          },
          id: s.id,
          hidden: false,
          highlighted: false,
          locked: false,
          selected: s.id === selectedId,
          color: s.color,
          initialColor: s.color,
        } as ComparisonAssessmentItem),
    );
  }
}
